import { zip } from '@mapbox/shp-write'
import { kml as xmlToGeoJSON } from '@tmcw/togeojson'
import { FeatureCollection as TurfFeatureCollection, rewind } from '@turf/turf'
import { FeatureCollection } from 'geojson'
import iconv from 'iconv-lite'
import JSZip from 'jszip'
import shp from 'shpjs'

import { GeoJSONObject } from '../types/geospatial'

export async function kmlFileToGeoJSON(file: File): Promise<GeoJSONObject> {
  const rawXml = await file.text()

  if (!rawXml) throw new Error('Invalid KML file')

  const xml = new DOMParser().parseFromString(rawXml.toString(), 'text/xml')

  const geojson = xmlToGeoJSON(xml)

  return geojson
}

export async function shpFilesToGeoJSON(files: File[]) {
  const requiredExtensions = ['shp', 'shx', 'dbf', 'prj', 'cpg']

  const groupedFiles = files.reduce((acc, file) => {
    const parts = file.name.split('.')
    if (parts.length < 2) return acc

    const ext = parts.pop()!
    const baseName = parts.join('.')

    if (!acc[baseName]) acc[baseName] = new Set()

    acc[baseName].add(ext)

    return acc
  }, {} as Record<string, Set<string>>)

  const hasAllRequiredExtensionsPerName = Object.values(groupedFiles).some(
    (extSet) =>
      requiredExtensions.every((requiredExt) => extSet.has(requiredExt)),
  )

  if (!hasAllRequiredExtensionsPerName)
    throw new Error('Os arquivos estão incompletos.')

  try {
    const content = await shpFilesToZip(files, { type: 'arraybuffer' })
    const geojson = await shp.parseZip(content)

    return geojson as GeoJSONObject | GeoJSONObject[]
  } catch (err: any) {
    throw new Error('Ocorreu um erro ao tentar ler o perímetro', { cause: err })
  }
}

interface ShpFileToZip<T extends JSZip.OutputType>
  extends JSZip.JSZipGeneratorOptions<T> {
  ext?: string[]
  filename?: string
  type: T
}

export async function shpFilesToZip<T extends JSZip.OutputType>(
  files: File[],
  {
    ext = ['.shp', '.shx', '.dbf', '.prj', '.cpg'],
    filename = files.at(0)?.name.split('.').at(0),
    ...options
  }: ShpFileToZip<T>,
) {
  if (!filename) throw new Error('Nome do arquivo não encontrado')

  const filesList = ext.map((ext: string) => filename.concat(ext))
  const zip = new JSZip()

  files.forEach(
    (file) => filesList.includes(file.name) && zip.file(file.name, file),
  )

  return zip.generateAsync(options)
}

export async function geoJSONToShapefile(
  geojson: FeatureCollection,
  filename: string,
): Promise<File[]> {
  const geojsonWithCounterClockwise = rewind(geojson as TurfFeatureCollection, {
    reverse: true,
  })

  const blob = await zip<'blob'>(
    geojsonWithCounterClockwise as FeatureCollection,
    {
      filename,
      compression: 'DEFLATE',
      outputType: 'blob',
      types: { polygon: filename },
    },
  )

  const zipInstance = await JSZip.loadAsync(blob)
  zipInstance.file(`${filename}.cpg`, 'UTF-8')

  const files: File[] = []
  const entries = Object.entries(zipInstance.files)

  for (const [path, zipEntry] of entries) {
    if (!zipEntry.dir) {
      const content = await zipEntry.async('blob')
      files.push(
        new File([content], path, {
          type: 'application/octet-stream',
          lastModified: Date.now(),
        }),
      )
    }
  }

  return files
}

export async function zipShapefileBufferToGeoJson(zipFileBuffer: ArrayBuffer) {
  const zip = await JSZip.loadAsync(zipFileBuffer, {
    decodeFileName: (bytes) => {
      if (!(bytes instanceof Uint8Array)) throw new Error('Invalid bytes')

      const buffer = Buffer.from(new Uint8Array(bytes))
      const filename = iconv.decode(buffer, 'cp850')
      return filename
    },
  })

  const fixedBuffer = await zip.generateAsync({ type: 'arraybuffer' })

  const geojson = await shp.parseZip(fixedBuffer)

  if (Array.isArray(geojson)) {
    throw new Error('O arquivo contém mais de um shapefile')
  }

  return geojson
}
